import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { api } from "api/config";
import { GetStationDetailById } from "api/stations";
import {
  user as readUser,
  defaultStation as readDefaultStation,
  setUser,
  setDefaultStation as persistDefaultStation,
} from "localStorage";
import { setUserData, setDefaultStation } from "slices/userSlice";

const userApi = api();

/**
 * Picks the station a page should pre-select from a station list: the operator's
 * default station when it is in the list, otherwise the first station (or null).
 */
export function pickDefaultStation(list, defaultStation) {
  if (!Array.isArray(list) || list.length === 0) return defaultStation || null;
  const match = list.find((s) => s.id === defaultStation?.id);
  return match ? defaultStation : list[0];
}

/**
 * Keeps the cached staff profile (and its assigned stations / default station)
 * in sync with the server on page entry.
 *
 * The station dropdowns on Scan / WhatsApp / Unassigned / BulkMove / MatchPackage
 * are built from the user's `stations` (and `defaultStation`) that were snapshotted
 * into localStorage at login time. When an admin assigns a new station to the user,
 * that snapshot goes stale and the new station only appears after a full re-login.
 *
 * This hook re-fetches `/user/me/profile` on mount, then refreshes the full default
 * station object, updating localStorage + Redux + the returned state. Pages should
 * consume the returned `{ user, defaultStation }` (instead of the bare localStorage
 * reads) and add `ready` to the dependency list of any effect that builds the
 * station list, so it re-runs once fresh data lands — no re-login required.
 *
 * Returns cached values immediately (no loading flash) and falls back to them if
 * the refresh fails.
 */
export default function useStationRefresh() {
  const dispatch = useDispatch();
  const [user, setUserState] = useState(() => readUser());
  const [defaultStation, setDefaultStationState] = useState(() => readDefaultStation());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await userApi.get("/user/me/profile");
        if (!active || !res.data?.success) return;
        const fresh = res.data.data;
        setUser(fresh);
        dispatch(setUserData(fresh));
        setUserState(fresh);

        // The login response stores the *full* default station object (with the
        // isSortStation / isPhotoRequired / final flags the Scan page reads), but
        // /user/me/profile only carries the lightweight {id,name,default} entries —
        // so fetch the full detail for the current default station.
        const defId = fresh.stations?.find((s) => s.default)?.id;
        if (defId) {
          const stRes = await GetStationDetailById(defId);
          if (!active) return;
          if (stRes?.success && stRes.data) {
            persistDefaultStation(stRes.data);
            dispatch(setDefaultStation(stRes.data));
            setDefaultStationState(stRes.data);
          }
        }
      } catch (e) {
        // Keep the cached profile on any failure — the page stays usable offline.
        console.warn("useStationRefresh: profile refresh failed", e?.message || e);
      } finally {
        if (active) setReady(true);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { user, defaultStation, ready };
}
